<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            {{ translate("admin_verification.verification_settings") }}
        </template>
        <template v-slot:body>
            <div class="columns">
                <div class="column is-12">
                    <h5 class="title is-6">{{ translate("admin_verification.lock_time") }}</h5>
                    <div class="field">
                        <div class="control">
                            <Checkbox2 v-model="localEarlyVerificationBlocked">
                                {{ translate("admin_verification.block_early_verification_btn") }}
                            </Checkbox2>
                        </div>
                    </div>

                    <div class="field">
                        <label class="label">
                            {{ translate("admin_verification.early_verification_period_desc") }}
                        </label>
                        <div class="control">
                            <input class="input" type="number" min="1" v-model="localEarlyVerificationPeriod" />
                        </div>
                    </div>

                    <h5 class="title is-6 mt-5">{{ translate("admin_verification.lock_queue") }}</h5>
                    <div class="field">
                        <div class="control">
                            <Checkbox2 v-model="localQueueVerificationBlocked">
                                {{ translate("admin_verification.block_queue_verification") }}
                            </Checkbox2>
                        </div>
                    </div>

                    <div class="field">
                        <label class="label">
                            {{ translate("admin_verification.queue_verification_limit_desc") }}
                        </label>
                        <div class="control">
                            <input class="input" type="number" min="1" v-model="localQueueVerificationLimit" />
                        </div>
                    </div>

                    <div class="field mt-5">
                        <div class="control">
                            <Checkbox2 v-model="localAutoDqWhenCategoryBeginsEnabled" :disabled="!isFullOrganizer">
                                ⚠️ {{ translate("admin_verification.auto_dq_unverified_when_category_begins") }}
                            </Checkbox2>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton class="button is-primary" @click="saveChanges" :is-loading="savingInProgress">
                    {{ translate("common.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import Checkbox2 from "../Common/Checkbox2.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import { useModal } from "../Common/useModal";
    import { useVerificationStore } from "./verificationStore";
    import eventSettingsApi from "../Api/eventSettingsApi";

    const props = defineProps<{
        isFullOrganizer: boolean;
    }>();

    const store = useVerificationStore();

    const { open: openModal, close, modal } = useModal();

    const localEarlyVerificationBlocked = ref(false);
    const localEarlyVerificationPeriod = ref<number | null>(null);
    const localQueueVerificationBlocked = ref(false);
    const localQueueVerificationLimit = ref<number | null>(null);
    const localAutoDqWhenCategoryBeginsEnabled = ref(false);
    const savingInProgress = ref(false);

    function open() {
        savingInProgress.value = false;
        localEarlyVerificationBlocked.value = store.earlyVerificationBlocked;
        localEarlyVerificationPeriod.value = store.earlyVerificationPeriod;
        localQueueVerificationBlocked.value = store.queueVerificationBlocked;
        localQueueVerificationLimit.value = store.queueVerificationLimit;
        localAutoDqWhenCategoryBeginsEnabled.value = store.autoDqWhenCategoryBeginsEnabled;
        openModal();
    }

    function saveChanges() {
        savingInProgress.value = true;
        const requests = [
            eventSettingsApi.updateSetting(store.eventId, "earlyVerificationBlocked", localEarlyVerificationBlocked.value),
            eventSettingsApi.updateSetting(store.eventId, "earlyVerificationPeriod", localEarlyVerificationPeriod.value!),
            eventSettingsApi.updateSetting(store.eventId, "queueVerificationBlocked", localQueueVerificationBlocked.value),
            eventSettingsApi.updateSetting(store.eventId, "queueVerificationLimit", localQueueVerificationLimit.value!),
        ];
        // Auto disqualification can be changed by full organizers only (same as before on the page).
        if (props.isFullOrganizer) {
            requests.push(
                eventSettingsApi.updateSetting(
                    store.eventId,
                    "autoDqWhenCategoryBeginsEnabled",
                    localAutoDqWhenCategoryBeginsEnabled.value,
                ),
            );
        }
        // TODO: Replace with a single bulk save endpoint, parallel requests can leave settings partly saved on failure.
        return Promise.all(requests)
            .then(() => {
                close();
                successToast(translate("common.changes_have_been_saved"));

                return store.fetchEarlyVerificationsSettings();
            })
            .catch(handleError)
            .finally(() => {
                savingInProgress.value = false;
            });
    }

    defineExpose({
        open,
    });
</script>

<style scoped></style>
